import Hls, {
  Events,
  type LevelLoadedData,
  type SubtitleTracksUpdatedData,
} from 'hls.js'
import { VIDEO_TYPES } from '../const/video'
import type HlsEngine from './HlsEngine'
import type { HlsEvent, Level } from './HlsEngine'

const MAX_BUFFER_LENGTH = {
  BEFORE_PLAYING: 12, // 3 x 4 seconds fragments
  AFTER_PLAYING: 32, // 8 x 4 seconds fragments
}

export class HlsjsImpl implements HlsEngine {
  private videoElement: HTMLVideoElement
  private hlsPlayer: Hls | null = null

  constructor(videoElement: HTMLVideoElement) {
    this.videoElement = videoElement

    if (Hls.isSupported()) {
      this.hlsPlayer = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        liveSyncDurationCount: 1,
        maxBufferLength: MAX_BUFFER_LENGTH.BEFORE_PLAYING,
        maxMaxBufferLength: MAX_BUFFER_LENGTH.BEFORE_PLAYING,
        maxLiveSyncPlaybackRate: 1.2,
        startLevel: -1,
        //capLevelToPlayerSize: true,
      })

      this.hlsPlayer.attachMedia(this.videoElement)
      this.hlsPlayer.subtitleDisplay = false // Disable default subtitle rendering
    }
  }

  async load(manifestUri: string) {
    try {
      // remove all existing sources
      while (this.videoElement.firstChild) {
        this.videoElement.removeChild(this.videoElement.firstChild)
      }
      const src = document.createElement('source')
      src.src = manifestUri
      src.type = 'application/x-mpegURL'
      this.videoElement.appendChild(src)
      await this.hlsPlayer!.loadSource(manifestUri)
    } catch (error) {
      console.error('Video loading error', error)
    }
  }

  getLiveSyncPosition(): number {
    return this.hlsPlayer?.liveSyncPosition || 0
  }

  setSubtitleTrack(trackId: number): void {
    if (!this.hlsPlayer) return
    this.hlsPlayer.subtitleTrack = trackId
  }

  addEventListener(event: HlsEvent, callback: (evt: CustomEvent) => void) {
    if (!this.hlsPlayer) return
    if (event === 'videotypechanged') {
      this.hlsPlayer.on(Hls.Events.LEVEL_LOADED, (_, data: LevelLoadedData) => {
        callback(
          new CustomEvent(event, {
            detail: {
              type:
                data.details.type === 'VOD'
                  ? VIDEO_TYPES.VOD
                  : VIDEO_TYPES.LIVE,
              isLive: data.details.live,
              dvrDuration: data.details.totalduration,
            },
          }),
        )
      })
    }
    if (event === 'ready') {
      this.hlsPlayer.on(Hls.Events.MEDIA_ATTACHED, (_, data) => {
        callback(new CustomEvent(event))
      })
    }
    /*this.hlsPlayer.on(Hls.Events.MANIFEST_PARSED, function (event, data) {
      console.log(
        'manifest loaded, found ' + data.levels.length + ' quality level',
        data.sessionData,
      )
    })
    this.hlsPlayer.on(Hls.Events.ERROR, function (event, data) {
      console.log('manifest loaded, found ', data)
    })*/
    if (event === 'subtitletracksloaded') {
      this.hlsPlayer.on(
        Hls.Events.SUBTITLE_TRACKS_UPDATED,
        (_, data: SubtitleTracksUpdatedData) =>
          callback(
            new CustomEvent(event, {
              detail: data.subtitleTracks.map((track) => ({
                id: track.id,
                label: track.name,
              })),
            }),
          ),
      )
    }
    if (event.startsWith('hls')) {
      this.hlsPlayer.on(event as Events, (_: any, data: any) => {
        callback(new CustomEvent(event, { detail: data }))
      })
    }
  }

  forceQuality(height: number | null) {
    if (!this.hlsPlayer) return
    if (height === null) {
      this.hlsPlayer.currentLevel = -1
      return
    }

    for (let i = 0; i < this.hlsPlayer.levels.length; i++) {
      if (this.hlsPlayer.levels[i].height == height) {
        this.hlsPlayer.currentLevel = i
        return
      }
    }
  }

  setMaxQuality(height: number) {
    if (!this.hlsPlayer) return

    const sortedLevels = this.hlsPlayer.levels.sort(
      (a, b) => a.height - b.height,
    )

    let index = -1
    for (let i = 0; i < sortedLevels.length; i++) {
      if (sortedLevels[i].height <= height) {
        index = i
      } else {
        break
      }
    }

    if (index >= 0) {
      this.hlsPlayer.autoLevelCapping = index
    }
  }

  getLevels(): Level[] {
    return (this.hlsPlayer?.levels || []).map((level) => ({
      height: level.height,
      width: level.width,
      bitrate: level.bitrate,
    }))
  }

  adaptPlayingBuffer() {
    if (!this.hlsPlayer) return
    this.hlsPlayer.config.maxBufferLength = MAX_BUFFER_LENGTH.AFTER_PLAYING
    this.hlsPlayer.config.maxMaxBufferLength = MAX_BUFFER_LENGTH.AFTER_PLAYING
  }
}
